/**
 * Utilidad para generar enlaces de "Agregar al calendario" externo.
 * Soporta Google Calendar, Outlook.com y descarga de archivo .ics.
 * @module utils/addToCalendar
 */

export interface CalendarEventData {
  title: string;
  startDate: Date;
  endDate: Date;
  description?: string;
  location?: string;
}

/**
 * Formatea una fecha en formato YYYYMMDDTHHmmssZ (UTC) para Google Calendar.
 */
function toGoogleCalendarDate(date: Date): string {
  return date.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
}

/**
 * Formatea una fecha en formato ISO 8601 para Outlook.com.
 */
function toOutlookDate(date: Date): string {
  return date.toISOString();
}

/**
 * Escapa caracteres especiales para el formato iCalendar.
 */
function escapeICalText(text: string): string {
  return text
    .replace(/\\/g, '\\\\')
    .replace(/;/g, '\\;')
    .replace(/,/g, '\\,')
    .replace(/\n/g, '\\n');
}

/**
 * Genera un UID único para el evento iCal.
 */
function generateUID(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}@migro-hiring`;
}

/**
 * Genera la URL para agregar un evento a Google Calendar.
 */
export function getGoogleCalendarUrl(event: CalendarEventData): string {
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: event.title,
    dates: `${toGoogleCalendarDate(event.startDate)}/${toGoogleCalendarDate(event.endDate)}`,
  });

  if (event.description) {
    params.set('details', event.description);
  }
  if (event.location) {
    params.set('location', event.location);
  }

  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

/**
 * Genera la URL para agregar un evento a Outlook.com.
 */
export function getOutlookCalendarUrl(event: CalendarEventData): string {
  const params = new URLSearchParams({
    rru: 'addevent',
    subject: event.title,
    startdt: toOutlookDate(event.startDate),
    enddt: toOutlookDate(event.endDate),
    allday: 'false',
    path: '/calendar/action/compose',
  });

  if (event.description) {
    params.set('body', event.description);
  }
  if (event.location) {
    params.set('location', event.location);
  }

  return `https://outlook.live.com/calendar/0/action/compose?${params.toString()}`;
}

/**
 * Genera el contenido de un archivo .ics (iCalendar) para Apple Calendar, Outlook de escritorio, etc.
 */
export function generateIcsContent(event: CalendarEventData): string {
  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Migro Hiring//CRM//ES',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `UID:${generateUID()}`,
    `DTSTART:${toGoogleCalendarDate(event.startDate)}`,
    `DTEND:${toGoogleCalendarDate(event.endDate)}`,
    `SUMMARY:${escapeICalText(event.title)}`,
  ];

  if (event.description) {
    lines.push(`DESCRIPTION:${escapeICalText(event.description)}`);
  }
  if (event.location) {
    lines.push(`LOCATION:${escapeICalText(event.location)}`);
  }

  lines.push(
    `DTSTAMP:${toGoogleCalendarDate(new Date())}`,
    'STATUS:CONFIRMED',
    'END:VEVENT',
    'END:VCALENDAR',
  );

  return lines.join('\r\n');
}

/**
 * Descarga un archivo .ics generado a partir de los datos del evento.
 */
export function downloadIcsFile(event: CalendarEventData, filename?: string): void {
  const content = generateIcsContent(event);
  const blob = new Blob([content], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.download = filename || `cita-migro-${event.startDate.toISOString().split('T')[0]}.ics`;

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  URL.revokeObjectURL(url);
}
